import Axios from 'axios';
import _ from 'lodash';

// utils
import { ApiError, RequestCancelledError } from '../../_lib/errors';
import { PubSub } from '../../utils/eventUtils';

import {
  cacheStaffLoginTokens,
  retrieveAuthTokens,
  storeFlowTokens,
} from '../../utils/next/auth.utils';
import { getPlatformRegion } from '../../utils';

const ApiInstance = Axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Z-Client-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

ApiInstance.interceptors.request.use(
  (config) => {
    const isPublic = checkPublicRoutes(config.url);
    const isSignedUrl = config.url.indexOf('amazonaws.') >= 0;

    if (isSignedUrl) {
      const newConfig = config;
      delete newConfig.headers.Authorization;
      return newConfig;
    }
    /*
     * Before the early return below, not after it. Axios serializes a multipart FormData body to
     * JSON unless the Content-Type is cleared, and a request that already carries an Authorization
     * header used to return above without ever reaching the fix — sending a multipart body
     * labelled application/json, with no boundary for anything downstream to parse.
     * https://github.com/axios/axios/issues/5556#issuecomment-1434668134
     */
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = undefined;
    }

    if (isPublic || config.headers.Authorization) {
      return config;
    }

    // Forward compatibility for next auth flow
    const { accessToken } = retrieveAuthTokens();
    config.headers.Authorization = `Bearer ${accessToken}`;

    config.headers['Accept-Language'] = getPlatformRegion() || 'en-US';

    return config;
  },
  (error) => Promise.reject(error)
);

const publicRoutes = [
  '/signup',
  '/login',
  '/loading',
  '/loading/token',
  '/validate/workflowId',
  '/wf/token',
  '/confirm',
  '/validateSignUp',
  '/reset?g-recaptcha-response',
  '/checkPasswordReset',
  '/validatePasswordReset',
  '/validateMFAReset',
  '/resendValEmail',
  '/join',
  '/validationToken',
  '/validationTokens',
  '/checkUserInvited',
  '/validateUserInvited',
  '/sso/token',
  '/sso/status',
  '/login/sitekey',
  '/application-signed',
];

// TODO, making a note that this could be a potential area of improvement for when we pass dynamic strings into our API paths
const whitelistUrls = [
  '/login/status',
  '/users/get-accounts',
  '/users/add-accounts',
  // Ends in /confirm, which publicRoutes substring-matches (legacy signup
  // flow) — without this override the request is sent WITHOUT an
  // Authorization header and the chat-agent returns 401.
  '/async-tasks/proposals',
];

function checkPublicRoutes(url: any) {
  const isWhitelisted = whitelistUrls.find(
    (partial) => url.indexOf(partial) !== -1
  );
  const isBlacklisted = publicRoutes.find(
    (partial) => url.indexOf(partial) !== -1
  );

  return !isWhitelisted && isBlacklisted;
}

ApiInstance.interceptors.response.use(
  (response) => {
    const url = _.get(response, 'request.responseURL');
    const isLogin =
      url &&
      (url.indexOf('/login') !== -1 ||
        url.indexOf('auth/v1/sso/token') !== -1 ||
        url.indexOf('auth/v1/signup?validationToken=') !== -1 ||
        url.indexOf('auth/v1/validateUserInvited') !== -1 ||
        url.indexOf('auth/v1/validatePasswordReset') !== -1 ||
        url.indexOf('auth/v1/validateMFAReset') !== -1 ||
        url.indexOf('auth/v1/join?validationToken') !== -1 ||
        url.indexOf('auth/v1/wf/token') !== -1 ||
        Boolean(url.match(/\/auth\/v1\/admin\/enter\/.+/, 'im')) ||
        Boolean(url.match(/\/auth\/v1\/valInvitedUser\/.+/, 'im')));

    // parse & set the token along with the user's scopes ONLY on login
    if (isLogin) {
      const accessToken = _.get(response, 'data.accessToken');
      const refreshToken = _.get(response, 'data.refreshToken');

      if (Boolean(accessToken) && Boolean(refreshToken)) {
        // A flow login or refresh is stored in sessionStorage only. Any other login is a staff
        // login, which also ends a flow session in this tab.
        if (url.indexOf('auth/v1/wf/token') !== -1) {
          storeFlowTokens(accessToken, refreshToken);
        } else {
          cacheStaffLoginTokens(accessToken, refreshToken);
        }
      }
    }

    return response;
  },
  (error) => {
    let apiEndpoint;
    try {
      apiEndpoint = new URL(error?.request?.responseURL);
    } catch {
      apiEndpoint = { pathname: error?.config?.url };
    } finally {
      if (
        apiEndpoint?.pathname?.startsWith('/api/auth') &&
        (error?.response?.status === 500 || error?.response?.status === 502)
      ) {
        PubSub.publish('api:error:5xx', {
          message:
            'Oops!! Something is Wrong with our Server. Please Try Again after Few Minutes!',
        });
      }
    }

    if (Axios.isCancel(error)) {
      // A typed error rather than a bare one, so a caller can tell a superseded request
      // from a request that actually failed. The message is unchanged for anything that
      // still reads it.
      return Promise.reject(new RequestCancelledError());
    }

    return Promise.reject(new ApiError(error));
  }
);

export default ApiInstance;
